/**
 * Production $/bdft by timber-list species.
 * Default species uses production.ratePerBdft / production.total.
 * Alternate species inherit that rate unless they set ratePerBdft.
 */

import type { ProductionNodeData, ProposalNodeSet, SpeciesTimberConfig } from '../types';
import { applyProfitMargin } from './proposalCalculations';

type ProductionRateSource = Pick<ProductionNodeData, 'boardFeet' | 'total' | 'ratePerBdft'>;
type SpeciesProductionNode = Pick<ProposalNodeSet, 'production' | 'materials'>;

export function isExplicitProductionRate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function defaultProductionRatePerBdft(
  production: ProductionRateSource | null | undefined,
  fallbackRate = 0,
): number {
  if (!production) return fallbackRate;
  if (isExplicitProductionRate(production.ratePerBdft) && production.ratePerBdft >= 0) {
    return production.ratePerBdft;
  }
  const bf = production.boardFeet || 0;
  const total = production.total || 0;
  if (bf > 0 && total > 0 && Number.isFinite(total / bf)) {
    return total / bf;
  }
  return fallbackRate;
}

export function isDefaultSpeciesName(
  ns: Pick<ProposalNodeSet, 'materials'>,
  speciesName?: string | null,
): boolean {
  const name = (speciesName || '').trim();
  if (!name || name.toLowerCase() === 'default') return true;
  const defaultName = (ns.materials?.timberList?.[0]?.species || '').trim();
  return !!defaultName && defaultName === name;
}

export function findAlternateSpecies(
  ns: Pick<ProposalNodeSet, 'materials'>,
  speciesName?: string | null,
): SpeciesTimberConfig | undefined {
  const name = (speciesName || '').trim();
  if (!name || isDefaultSpeciesName(ns, name)) return undefined;
  return (ns.materials?.alternateSpecies || []).find(s => s.speciesName === name);
}

export function productionRateForSpecies(
  ns: SpeciesProductionNode,
  speciesName?: string | null,
  fallbackRate = 0,
): number {
  const defaultRate = defaultProductionRatePerBdft(ns.production, fallbackRate);
  const alt = findAlternateSpecies(ns, speciesName);
  if (alt && isExplicitProductionRate(alt.ratePerBdft) && alt.ratePerBdft >= 0) {
    return alt.ratePerBdft;
  }
  return defaultRate;
}

/** Raw production for a species. Default species stays on stored production.total. */
export function productionRawForSpecies(
  ns: SpeciesProductionNode,
  speciesName?: string | null,
  fallbackRate = 0,
): number {
  if (isDefaultSpeciesName(ns, speciesName) || !findAlternateSpecies(ns, speciesName)) {
    return ns.production?.total || 0;
  }
  const bf = ns.production?.boardFeet || 0;
  return bf * productionRateForSpecies(ns, speciesName, fallbackRate);
}

export function speciesTimberKitRawDelta(
  ns: SpeciesProductionNode,
  speciesName?: string | null,
  fallbackRate = 0,
): { timberDiff: number; productionDiff: number; rawDelta: number } {
  const alt = findAlternateSpecies(ns, speciesName);
  if (!alt) {
    return { timberDiff: 0, productionDiff: 0, rawDelta: 0 };
  }
  const timberDiff = (alt.timberTotal || 0) - (ns.materials?.timberTotal || 0);
  const productionDiff = productionRawForSpecies(ns, speciesName, fallbackRate) - (ns.production?.total || 0);
  return { timberDiff, productionDiff, rawDelta: timberDiff + productionDiff };
}

/** Default timber-kit billable plus margin(timberDiff + productionDiff) × qty. */
export function applySpeciesTimberKitBillableDelta(
  ns: SpeciesProductionNode,
  speciesName: string | null | undefined,
  defaultTimberKitBillable: number,
  profitMargin: number,
  quantity = 1,
  fallbackRate = 0,
): number {
  const { rawDelta } = speciesTimberKitRawDelta(ns, speciesName, fallbackRate);
  if (!rawDelta) return defaultTimberKitBillable;
  return defaultTimberKitBillable + Math.round(applyProfitMargin(rawDelta, profitMargin) * quantity);
}

/**
 * Prefer baked `billablePrices.speciesOptions` (already includes production).
 * Fall back to a live timber+production delta for older proposals.
 */
export function speciesTimberKitBillable(
  ns: SpeciesProductionNode,
  speciesName: string | null | undefined,
  defaultTimberKitBillable: number,
  profitMargin: number,
  quantity = 1,
  bakedSpeciesOptions?: Record<string, number> | null,
  fallbackRate = 0,
): number {
  const name = (speciesName || '').trim();
  if (name && name.toLowerCase() !== 'default' && bakedSpeciesOptions?.[name] !== undefined) {
    return bakedSpeciesOptions[name];
  }
  return applySpeciesTimberKitBillableDelta(
    ns,
    speciesName,
    defaultTimberKitBillable,
    profitMargin,
    quantity,
    fallbackRate,
  );
}

export type SpeciesProductionAuditLine = {
  speciesName: string;
  isDefault: boolean;
  ratePerBdft: number;
  inheritedRate: boolean;
  boardFeet: number;
  raw: number;
  productionDiff: number;
};

export function speciesProductionAuditLines(
  ns: SpeciesProductionNode,
  fallbackRate = 0,
): SpeciesProductionAuditLine[] {
  const boardFeet = ns.production?.boardFeet || 0;
  const defaultRate = defaultProductionRatePerBdft(ns.production, fallbackRate);
  const defaultName = (ns.materials?.timberList?.[0]?.species || '').trim() || 'Default';
  const defaultRaw = ns.production?.total || 0;
  const lines: SpeciesProductionAuditLine[] = [
    {
      speciesName: defaultName,
      isDefault: true,
      ratePerBdft: defaultRate,
      inheritedRate: false,
      boardFeet,
      raw: defaultRaw,
      productionDiff: 0,
    },
  ];
  if (!ns.materials?.quoteMultipleSpecies) return lines;
  for (const alt of ns.materials.alternateSpecies || []) {
    const inherited = !isExplicitProductionRate(alt.ratePerBdft) || alt.ratePerBdft < 0;
    const raw = productionRawForSpecies(ns, alt.speciesName, fallbackRate);
    lines.push({
      speciesName: alt.speciesName,
      isDefault: false,
      ratePerBdft: productionRateForSpecies(ns, alt.speciesName, fallbackRate),
      inheritedRate: inherited,
      boardFeet,
      raw,
      productionDiff: raw - defaultRaw,
    });
  }
  return lines;
}

export type SpeciesTimberAuditLine = {
  speciesName: string;
  isDefault: boolean;
  timberTotal: number;
  timberDiff: number;
};

export function speciesTimberAuditLines(ns: Pick<ProposalNodeSet, 'materials'>): SpeciesTimberAuditLine[] {
  const defaultName = (ns.materials?.timberList?.[0]?.species || '').trim() || 'Default';
  const defaultTimber = ns.materials?.timberTotal || 0;
  const lines: SpeciesTimberAuditLine[] = [
    { speciesName: defaultName, isDefault: true, timberTotal: defaultTimber, timberDiff: 0 },
  ];
  if (!ns.materials?.quoteMultipleSpecies) return lines;
  for (const alt of ns.materials.alternateSpecies || []) {
    const timberTotal = alt.timberTotal || 0;
    lines.push({
      speciesName: alt.speciesName,
      isDefault: false,
      timberTotal,
      timberDiff: timberTotal - defaultTimber,
    });
  }
  return lines;
}
